import { useMemo, useState } from "react";

import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { MetricsExplorerProvider } from "@features/metrics-explorer";
import { type TileDescriptor } from "@features/metrics-explorer/types";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import { type ITimeGranularityV2 } from "@north-beam/nb-common";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { useSalesV4Context, useTableStateDimensions } from "../hooks";
import { getSelectionForSelectedTab, selectedTabToLevel } from "../utils";

import { SalesChart } from "./sales-chart";
import { MetricsExplorerDrawer } from "./sales-metrics-explorer";

export const SalesChartContainer = () => {
  const { isDemoMode } = usePrefs();
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();
  const { dateRange, timeGranularity, tableStates, chartState, metrics } =
    useSalesV4Context();
  const { breakdownDimension } = useTableStateDimensions(tableStates[0]);

  const [hiddenTiles, setHiddenTiles] = useState<string[]>([]);
  const [isMetricsExplorerVisible, setIsMetricsExplorerVisible] =
    useState(false);

  const metricsExplorerFilters = useMemo(() => {
    return {
      accountingMode,
      attributionModel,
      attributionWindow,
      timeGranularity,
      dateRange,
      filterQuery: null,
      advancedSearch: null,
      level: selectedTabToLevel(tableStates[0].selectedTab),
    };
  }, [
    accountingMode,
    attributionModel,
    attributionWindow,
    dateRange,
    timeGranularity,
    tableStates,
  ]);

  const metricsExplorerChartState = useMemo(() => {
    return {
      alignAxesScale: chartState.alignAxesScale,
    };
  }, [chartState.alignAxesScale]);

  const tiles = useMemo(() => {
    const tiles: TileDescriptor[] = [];
    const selection = getSelectionForSelectedTab(tableStates[0]);

    if (selection.length === 0) {
      for (const [index, chartTile] of chartState.metrics.entries()) {
        tiles.push({
          ...cloneDeep(chartTile),
          id: uuidv4(),
          active: index === 0,
          visible: true,
          index,
        });
      }
    } else {
      let index = 0;

      for (const [chartTileIndex, chartTile] of chartState.metrics.entries()) {
        if (
          chartState.displayMode === "aggregated" ||
          (chartState.displayMode === "stacked" && chartTileIndex > 0)
        ) {
          const filters = [];
          const advancedSearches = [];

          for (const selectionRow of selection) {
            if (
              tableStates[0].selectedTab === "breakdown" &&
              breakdownDimension.id === "date"
            ) {
              advancedSearches.push({
                "==": [{ var: breakdownDimension.id }, selectionRow.id],
              });
            } else if (tableStates[0].selectedTab === "breakdown") {
              filters.push({
                breakdown: breakdownDimension.name,
                value: selectionRow.id,
              });
            } else {
              advancedSearches.push({
                "==": [{ var: "adKey" }, selectionRow.id],
              });
            }
          }

          tiles.push({
            ...cloneDeep(chartTile),
            id: uuidv4(),
            filters,
            advancedSearch:
              advancedSearches.length > 0 ? { or: advancedSearches } : null,
            active: index === 0,
            visible: true,
            index,
          });

          index += 1;
        } else {
          for (const selectionRow of selection) {
            const metric = metrics.find((e) => e.id === chartTile.metricId);

            const filters = [];
            let advancedSearch = null;

            if (
              tableStates[0].selectedTab === "breakdown" &&
              breakdownDimension.id === "date"
            ) {
              advancedSearch = {
                "==": [{ var: breakdownDimension.id }, selectionRow.id],
              };
            } else if (tableStates[0].selectedTab === "breakdown") {
              filters.push({
                breakdown: breakdownDimension.name,
                value: selectionRow.id,
              });
            } else {
              advancedSearch = { "==": [{ var: "adKey" }, selectionRow.id] };
            }

            const baseName = chartTile.name || metric?.name;

            const name = `${baseName}: ${generateAnonBase64Hash({
              value: selectionRow.label,
              prefix: "Name",
              doAnon: isDemoMode,
            })}`;

            tiles.push({
              ...cloneDeep(chartTile),
              id: uuidv4(),
              name,
              filters,
              advancedSearch,
              active: index === 0,
              visible: true,
              index,
              groupId: chartTile.id,
              groupName: baseName,
              isStacked: chartState.displayMode === "stacked",
            });

            index += 1;
          }
        }
      }
    }

    return tiles;
  }, [
    isDemoMode,
    metrics,
    breakdownDimension,
    chartState.metrics,
    chartState.displayMode,
    tableStates[0].selectedTab,
    tableStates[0].selectedBreakdownId,
    tableStates[0].adSelection,
    tableStates[0].adsetSelection,
    tableStates[0].campaignSelection,
    tableStates[0].breakdownSelection,
  ]);

  const tilesWithVisibility = useMemo(() => {
    return tiles.map((e) => ({ ...e, visible: !hiddenTiles.includes(e.id) }));
  }, [tiles, hiddenTiles]);

  return (
    <div className="flex-1 overflow-hidden">
      <MetricsExplorerProvider
        filters={metricsExplorerFilters}
        chartState={metricsExplorerChartState}
        tiles={tilesWithVisibility}
        setAlignAxesScale={() => null}
        setTiles={(tiles) => {
          setHiddenTiles(tiles.filter((e) => !e.visible).map((e) => e.id));
        }}
      >
        <SalesChart
          onOpenMetricsExplorer={() => setIsMetricsExplorerVisible(true)}
        />
      </MetricsExplorerProvider>
      <MetricsExplorerDrawer
        isOpen={isMetricsExplorerVisible}
        tiles={tiles}
        filters={metricsExplorerFilters}
        chartState={chartState}
        onClose={() => setIsMetricsExplorerVisible(false)}
      />
    </div>
  );
};
